import { AlertCircle, RefreshCw, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { StatusPill } from './StatusPill'

export type ServiceStatus = 'ok' | 'degraded' | 'error' | 'unknown'

export interface SubMetric {
  label: string
  value: React.ReactNode  // accept ReactNode so consumers can pass numbers, formatted strings, badges
  hint?: string
}

interface ServiceHealthCardProps {
  title: string
  url?: string
  status: ServiceStatus
  statusLabel?: string
  isLoading?: boolean
  isError?: boolean
  errorMessage?: string
  subMetrics?: SubMetric[]
  onRetry?: () => void
  isRetrying?: boolean
  children?: React.ReactNode
  className?: string
}

// ServiceStatus → StatusPill status (StatusPill knows the colors)
const STATUS_PILL_MAP: Record<ServiceStatus, 'ok' | 'warning' | 'error' | 'unknown'> = {
  ok: 'ok',
  degraded: 'warning',
  error: 'error',
  unknown: 'unknown',
}

/**
 * Shared card primitive for the Backend Services section. Each consumer
 * computes its own status (the primitive doesn't know per-service status
 * semantics) and passes it via the `status` prop.
 *
 * Layout: header (title + status pill + optional URL link) + sub-metrics
 * grid (label-value pairs, 2-col on desktop, 1-col on mobile) + optional
 * children slot for service-specific custom content (e.g. a wallet table).
 */
export function ServiceHealthCard({
  title,
  url,
  status,
  statusLabel,
  isLoading,
  isError,
  errorMessage,
  subMetrics,
  onRetry,
  isRetrying,
  children,
  className,
}: ServiceHealthCardProps) {
  if (isLoading) {
    return (
      <div className={cn('rounded-lg border bg-card p-4 sm:p-6 shadow-sm', className)}>
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="h-5 w-32 bg-muted rounded animate-pulse" />
          <div className="h-5 w-16 bg-muted rounded-full animate-pulse" />
        </div>
        <div className="space-y-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-4 w-full bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={cn('rounded-lg border border-status-error/30 bg-status-error/10 p-4 sm:p-6 shadow-sm', className)}>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <h4 className="text-sm sm:text-base font-semibold font-mono">{title}</h4>
            <StatusPill status="error" label={statusLabel ?? 'error'} />
          </div>
        </div>
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-status-error shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-status-error">{errorMessage ?? 'Service is unreachable.'}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                disabled={isRetrying}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-status-error/30 text-xs text-status-error hover:bg-status-error/10 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isRetrying && 'animate-spin')} />
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('rounded-lg border bg-card p-4 sm:p-6 shadow-sm', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-sm sm:text-base font-semibold font-mono truncate">{title}</h4>
          <StatusPill status={STATUS_PILL_MAP[status]} label={statusLabel ?? status} />
        </div>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title={url}
          >
            <span className="hidden sm:inline truncate max-w-[200px]">{url.replace(/^https?:\/\//, '')}</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {/* Sub-metrics grid */}
      {subMetrics && subMetrics.length > 0 && (
        <div className="grid gap-x-4 gap-y-2 grid-cols-1 sm:grid-cols-2">
          {subMetrics.map(m => (
            <div key={m.label} className="flex items-baseline justify-between gap-2 py-1" title={m.hint}>
              <span className="text-xs text-muted-foreground">{m.label}</span>
              <span className="text-sm font-medium text-right truncate">{m.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Custom content slot */}
      {children && <div className={cn(subMetrics && subMetrics.length > 0 && 'mt-4')}>{children}</div>}
    </div>
  )
}
